import { FC, useRef } from 'react';
import { useHubspotForm } from '@aaronhayes/react-use-hubspot-form';
import { Helmet } from 'react-helmet';
import { OtpForm } from '@/components';
import { COMMON_CONTAINER_ID, navigationPages } from '@/config';
import { UserPreferenceEnum } from '@/api';
import { ToastContainer } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';

import amlbotLogo from '@/assets/images/logo-amlbot.svg';

import VideoGuide from './VideoGuide';

import styles from './TraceSignUp.module.sass';

const PORTAL_ID = (import.meta.env.VITE_REACT_APP_HUBSPOT_PORTAL_ID as string)!;
const SIGNUP_FORM_ID = (import.meta.env
  .VITE_REACT_APP_HUBSPOT_SIGNUP_FORM_ID as string)!;

const formDivId = 'hubspot-form-wrapper';

const TraceSignUp: FC = () => {
  const hubspotFormRef = useRef<HTMLFormElement | null>(null);
  const [searchParams] = useSearchParams();
  const txid = searchParams.get('txid')?.trim() || '';

  useHubspotForm({
    portalId: PORTAL_ID,
    formId: SIGNUP_FORM_ID,
    target: `#${formDivId}`,
    onFormReady: (form) => {
      hubspotFormRef.current = form;
    },
  });

  const handleBeforeVerify = (email: string) => {
    try {
      const form = hubspotFormRef.current;
      if (form) {
        const emailInput = form.querySelector(
          'input[name="email"]',
        ) as HTMLInputElement;
        emailInput.value = email;
        form.submit();
      }
    } catch (err) {
      console.log('HUBSPOT ERROR', err);
    }
  };

  const handleSuccess = (token: string) => {
    window.location.href = navigationPages.TRACE_INVESTIGATIONS.path;
  };

  return (
    <>
      <Helmet>
        <title>AMLBot - AI Trace Signup</title>
        <meta
          name="description"
          content="AMLBot AI Trace Investigation signup"
        />
      </Helmet>

      <div className={styles.page}>
        <div className={styles.wrapper}>
          <img src={amlbotLogo} alt="AMLBot" className={styles.logo} />

          <div className={styles.content}>
            <h1 className={styles.title}>
              Start Investigating with AI-Powered Trace
            </h1>
            <p className={styles.description}>
              Unlock advanced blockchain trace investigations powered by AI.
              Watch a quick walkthrough to see how AI Tracer works.
            </p>

            <div className={styles.formColumn}>
              <OtpForm
                preference={UserPreferenceEnum.AI_TRACE}
            traceHashFor={txid}
                stepTitles={{
                  1: 'Create your account to access AI Trace',
                  2: 'Enter the OTP code we sent to your email to continue',
                }}
                onBeforeVerify={handleBeforeVerify}
                onSuccess={handleSuccess}
              />
            </div>
          </div>

          <div className={styles.videoColumn}>
            <VideoGuide />
          </div>
        </div>
      </div>

      <ToastContainer
        containerId={COMMON_CONTAINER_ID}
        position="top-center"
        limit={3}
        enableMultiContainer
        hideProgressBar
      />
      <div id={formDivId} style={{ height: 0, width: 0 }} />
    </>
  );
};

export default TraceSignUp;
